import {
	CartesianChartDataOptions,
	ChartDataOptions,
	StyledMeasureColumn,
} from '@sisense/sdk-ui';
import { ExtendedChartWidgetProps } from '../types';

function isCartesianChartDataOptions(
	dataOptions: ChartDataOptions | undefined,
): dataOptions is CartesianChartDataOptions {
	return (
		dataOptions != null &&
		typeof dataOptions === 'object' &&
		'value' in dataOptions &&
		Array.isArray((dataOptions as CartesianChartDataOptions).value)
	);
}

function getMeasureKey(m: StyledMeasureColumn, index: number): string {
	// Prefer column id when available, otherwise use name, fall back to index
	const col = m.column;
	if (col && 'id' in col && col.id != null) {
		return `id:${col.id}`;
	}
	if (col?.name != null && col.name !== '') {
		return `name:${col.name}`;
	}
	return `index:${index}`;
}

function mergeValueMeasureFlags(
	prior: StyledMeasureColumn[],
	next: StyledMeasureColumn[],
): StyledMeasureColumn[] {
	const byKey = new Map(prior.map((m, i) => [getMeasureKey(m, i), m]));
	return next.map((m, i) => {
		const p = byKey.get(getMeasureKey(m, i));
		if (!p) return m;
		return {
			...m,
			...(typeof p.showOnRightAxis === 'boolean' && {
				showOnRightAxis: p.showOnRightAxis,
			}),
			...(p.color !== undefined && { color: p.color }),
		};
	});
}

/**
 * Copies chart type, style options, and per-measure visual flags (right-axis, color)
 * from a prior chart onto the newly generated widget props.
 *
 * NOTE: sort / trend / forecast are intentionally NOT copied — they reference column
 * names that may no longer exist after structural changes (add/remove/swap dimensions).
 */
export function applyPriorChartStyle(
	widgetProps: ExtendedChartWidgetProps,
	priorChart: ExtendedChartWidgetProps,
): void {
	widgetProps.chartType = priorChart.chartType ?? widgetProps.chartType;
	widgetProps.styleOptions =
		priorChart.styleOptions ?? widgetProps.styleOptions;

	// Preserve per-measure flags by stable key — renamed measures won't match (expected).
	if (
		isCartesianChartDataOptions(priorChart.dataOptions) &&
		isCartesianChartDataOptions(widgetProps.dataOptions)
	) {
		const priorValue = priorChart.dataOptions.value;
		const nextValue = widgetProps.dataOptions.value;
		if (Array.isArray(priorValue) && Array.isArray(nextValue)) {
			widgetProps.dataOptions.value = mergeValueMeasureFlags(
				priorValue as StyledMeasureColumn[],
				nextValue as StyledMeasureColumn[],
			);
		}
	}
}
